import { useRef, useEffect, useState, useMemo } from 'react';
import PropTypes from 'prop-types';

import '../MCPServerSetting.css';
import Utils from '@/common/utils/utils';
import Constant from '@/common/constant/Constant';
import McpServerApi from '@/common/constant/api/McpServerApi';
import OverFlowTooltipWithTabsMui from '@/component/common/OverFlowTooltipWithTabsMui';
import { renderSettingMCPServer } from '../handler/MCPServerUIHandler';
import { handleUpdateMCPServerByAction} from '../handler/MCPServerLogicHandler';
import { toggleLocalMcpInProject } from '@/service/mcpServerService';
import { LOCAL_MCP_LABEL_PREFIX, LOCAL_MCP_USER_PREFIX, LOCAL_MCP_PROJECT_PREFIX } from '@/component/mcpCatalog/McpCatalogConstant';
import { parseLocalMcpScope } from '@/common/utils/mcpUtils';
import ConfirmDialog from '@/common/dialog/ConfirmDialog';

import { useRecoilState, useSetRecoilState, useRecoilValue } from 'recoil';
import { closeTabId, multiPanelAtom, mcpActivationChangedAtom } from '@/state/atom/panelAtom';
import { closePanelTabSelector } from '@/state/selector/panelSelector';
import trI18n from '@/common/utils/trI18n';
import { useTranslationSubscription } from '@/hooks/useTranslation';

MCPServerListInProject.propTypes = {
    panelType: PropTypes.string,
    setEditApiData: PropTypes.func,
    setIsLoading: PropTypes.func,
    corporationDetail: PropTypes.object,
    projectInuseDetail: PropTypes.object,
    userDetail: PropTypes.object,
    setListSettingApiProject: PropTypes.array,
    listSettingApiProject: PropTypes.array,
    props: PropTypes.object,
    localMcpList: PropTypes.array,
    setLocalMcpList: PropTypes.func,
    projectLocalMcpList: PropTypes.array,
    setProjectLocalMcpList: PropTypes.func
};

function MCPServerListInProject({
    setIsLoading,
    panelType,
    setEditApiData,
    corporationDetail,
    projectInuseDetail,
    userDetail,
    listSettingApiProject,
    setListSettingApiProject,
    props,
    localMcpList = [],
    setLocalMcpList,
    projectLocalMcpList = [],
    setProjectLocalMcpList
}) {
    // Re-render when the translation dictionary loads/updates.
    useTranslationSubscription();

    const mcpServerListBody = useRef(null);

    const [closeTabIdState, setCloseTabIdState] = useRecoilState(closeTabId);
    const closePanelTab = useSetRecoilState(closePanelTabSelector);
    const panelList = useRecoilValue(multiPanelAtom);
    const setMcpActivationChanged = useSetRecoilState(mcpActivationChangedAtom);

    const [isShowPopUpReload, setIsShowPopUpReload] = useState(false);

    const callToggleLocalMcp = (catId, newValue) => {
        const { catId: rawCatId, prefix } = parseLocalMcpScope(catId);
        const setList = prefix === LOCAL_MCP_PROJECT_PREFIX ? setProjectLocalMcpList : setLocalMcpList;

        setList(prev =>
            prev.map(item => item.catId === catId ? { ...item, isActive: newValue } : item)
        );
        setIsLoading(true);
        toggleLocalMcpInProject(catId, newValue, corporationDetail.id, projectInuseDetail.id, userDetail.id)
            .then(res => {
                if (res?.success) {
                    const subKey = prefix === LOCAL_MCP_PROJECT_PREFIX ? 'projectLocalMcp' : 'userLocalMcp';
                    setMcpActivationChanged(prev => ({
                        ...prev,
                        [subKey]: { ...(prev?.[subKey] || {}), [rawCatId]: newValue }
                    }));
                }
                if (!res?.success) {
                    setList(prev =>
                        prev.map(item => item.catId === catId ? { ...item, isActive: !newValue } : item)
                    );
                    if (res?.error === Constant.DATA_CHANGED_PLEASE_RELOAD_ERROR) {
                        setIsShowPopUpReload(true);
                    }
                }
            })
            .catch(err => {
                console.log('callToggleLocalMcp err: ', err);
                setList(prev =>
                    prev.map(item => item.catId === catId ? { ...item, isActive: !newValue } : item)
                );
            })
            .finally(() => {
                setIsLoading(false);
            });
    };

    const isEnableMCPService = useMemo(() => {
        if (Utils.isEmptyObject(corporationDetail)) return false;
        if (corporationDetail.contractType !== Constant.CORPORATION_CONTRACT_TYPE.PREMIUM) return false;
    
        // current corporation setting MCP Server
        const currentCorporationSetting = Utils.convertJSONDataToObject(
            corporationDetail?.corporationSetting
        );
        const currentCorporationLimitValue = corporationDetail?.limitValue;
        const isRootOnMCPServerOfCurrentCorporation =
            currentCorporationLimitValue?.mcpServer === Constant.STATUS_SERVICE_PERMISSION.ON;
        const usingMCPServerOfCurrentCorporation =
            currentCorporationSetting?.usingMCPServer === Constant.STATUS_SERVICE_PERMISSION.ON;
        
        if (!isRootOnMCPServerOfCurrentCorporation || !usingMCPServerOfCurrentCorporation) return false;

        return true;
    }, [ corporationDetail ]);

    const isActiveProject = apiData => {
        let projectActived = apiData?.projectActived || [];
        let isActivePrj = projectActived?.includes(projectInuseDetail?.id);
        return isActivePrj;
    };

    const callActiveInProject = (activeValue, mcpServerData) => {
        if (!isEnableMCPService) {
            setIsShowPopUpReload(true);
            return;
        }
        const projectId = projectInuseDetail?.id;

        const projectActived = activeValue
            ? [...(mcpServerData?.projectActived || []), projectId]
            : (mcpServerData?.projectActived || []).filter(id => id !== projectId);

        const mcpData = {
            id: mcpServerData.id,
            projectActived
        };
        handleUpdateSuccess(null, mcpServerData, projectActived)
        setIsLoading(true);
        handleUpdateMCPServerByAction(
            McpServerApi.MCP_SERVER_UPDATE_ACTION_TYPE.TOGGLE_ACTIVE_IN_ORG,
            mcpData,
            {
                corporationInuse: corporationDetail.id,
                projectInuse: projectInuseDetail.id,
                userId: userDetail.id,
                panelType
            }
        )
            .then(response => handleUpdateSuccess(response, mcpServerData, projectActived))
            .catch(err => {
                console.log('callActiveInProject err: ', err);
            })
            .finally(() => {
                setIsLoading(false);
            });
    };

    const handleUpdateSuccess = (response, mcpServerData, projectActived) => {
        if (!Utils.isEmptyString(response?.error)) {
            if (response?.error === 'COMMSG030') {
                setIsShowPopUpReload(true);
            }
        }
        setListSettingApiProject(prev =>
            prev.map(item =>
                item.id === mcpServerData?.id
                    ? { ...item, projectActived }
                    : item
            )
        );
    };

    useEffect(() => {
        if (props.tabId === closeTabIdState) {
            let currentPanelInfo = Utils.getPanelInfo(props.panelId, panelList);
            if (!currentPanelInfo || Utils.isEmptyObject(currentPanelInfo)) return;
            closePanelTab({
                event: null,
                panelType: currentPanelInfo.type,
                panelId: props.panelId,
                tabId: props.tabId
            });
            setCloseTabIdState(0);
        }
    }, [closeTabIdState]);

    return (
        <div style={{ height: '100%' }}>
            <div className="mcp-server-header mcp-server-line-space">
                <div className="mcp-server-header-inner">
                    <h3 className="mcp-server-setting-list">
                        {trI18n('MCP_SERVER_SETTING_LIST')}
                    </h3>
                </div>
            </div>
            <div className="mcp-server-body">
                {!Utils.isEmptyArray(listSettingApiProject) && (<div className="mcp-server-body-line mcp-server-line-space">
                    <div className="">
                        <h4>{trI18n('MCP_SERVER_SETTING_LABEL_PROJECT')}</h4>
                    </div>
                    <div className="mcp-server-last-title">
                        <h4>{trI18n('MCP_SERVER_SETTING_ACTIVE_IN_PROJECT')}</h4>
                    </div>
                </div>) }
                
                {!Utils.isEmptyArray(listSettingApiProject) && <div className="mcp-server-list" ref={mcpServerListBody}>
                    {
                        listSettingApiProject.map(apiData => (
                            <div key={apiData.id} className="mcp-server-body-line mcp-server-line">
                                <button
                                    className="mcp-server-item btn-reset-default cursor-default"
                                >
                                    <OverFlowTooltipWithTabsMui value={Utils.serverNameWithPrefix(apiData.serverName, apiData.type)} />
                                </button>
                                {renderSettingMCPServer(
                                    isActiveProject(apiData),
                                    callActiveInProject,
                                    apiData,
                                    false,
                                    panelType
                                )}
                            </div>
                        ))}
                </div> }
                {/* [P] Project-level Local MCP */}
                {isEnableMCPService && !Utils.isEmptyArray(projectLocalMcpList) && (
                    <div className="mcp-server-list">
                        {projectLocalMcpList.map(item => (
                            <div key={item.catId} className="mcp-server-body-line mcp-server-line">
                                <div className="mcp-server-item">
                                    <OverFlowTooltipWithTabsMui value={`${LOCAL_MCP_LABEL_PREFIX}${LOCAL_MCP_PROJECT_PREFIX}${item.mcpName}`} />
                                </div>
                                {renderSettingMCPServer(
                                    item.isActive,
                                    (value) => callToggleLocalMcp(item.catId, value),
                                    { ...item, id: item.catId },
                                    false,
                                    panelType
                                )}
                            </div>
                        ))}
                    </div>
                )}
                {isEnableMCPService && !Utils.isEmptyArray(localMcpList) && (
                    <>
                        <div className="mcp-server-body-line mcp-server-line-space">
                            <div className="">
                                <h4>{trI18n('LOCAL_MCP')}</h4>
                            </div>
                            <div className="mcp-server-last-title">
                                <h4>{trI18n('MCP_SERVER_SETTING_ACTIVE_IN_PROJECT')}</h4>
                            </div>
                        </div>
                        <div className="mcp-server-list">
                            {localMcpList.map(item => (
                                <div key={item.catId} className="mcp-server-body-line mcp-server-line">
                                    <div className="mcp-server-item">
                                        <OverFlowTooltipWithTabsMui value={`${LOCAL_MCP_LABEL_PREFIX}${LOCAL_MCP_USER_PREFIX}${item.mcpName}`} />
                                    </div>
                                    {renderSettingMCPServer(
                                        item.isActive,
                                        (value) => callToggleLocalMcp(item.catId, value),
                                        {...item, id: item.catId},
                                        false,
                                        panelType
                                    )}
                                </div>
                            ))}
                        </div>
                    </>
                )}
            </div>
            <ConfirmDialog
                isOpen={isShowPopUpReload}
                msgConfirm={trI18n('COMMSG030')}
                okCallBack={() => window.location.reload()}
            />
        </div>
    );
}

export default MCPServerListInProject;
